import Cookies from 'js-cookie';

import config from '@/config';
import { ROUTES_API } from '@/shared/config/routes';
import { joinBaseUrl } from '@/shared/utils/url';

const ACCESS_TOKEN_EXPIRATION_DAYS = 365;

const COOKIES = {
	accessToken: 'token',
	temporaryToken: 'temporaryToken',
	tokenValidUntil: 'token_valid_until',
	secureSession: '__Secure-session',
} as const;

const AUTHENTICATION_COOKIE_KEYS = [
	COOKIES.accessToken,
	COOKIES.temporaryToken,
	COOKIES.tokenValidUntil,
	COOKIES.secureSession,
];

const isSecure = () => {
	return typeof window !== 'undefined' && window.location.protocol === 'https:';
};

const isAuthenticationCookie = (key: string) => {
	return AUTHENTICATION_COOKIE_KEYS.includes(key as (typeof AUTHENTICATION_COOKIE_KEYS)[number]);
};

const getAuthenticationCookieName = (key: string) => {
	return `${key}${config.authentication_cookies_suffix || ''}`;
};

const getCookie = (key: string) => {
	if (isAuthenticationCookie(key)) {
		const authCookie = Cookies.get(getAuthenticationCookieName(key));

		if (authCookie) {
			return authCookie;
		}
	}

	return Cookies.get(key);
};

const setCookie = (key: string, value: string | number | boolean, options: Cookies.CookieAttributes = {}) => {
	if (isAuthenticationCookie(key)) {
		const authCookieOptions = { ...options };

		if (config.authentication_cookies_domain) {
			authCookieOptions.domain = config.authentication_cookies_domain;
		}

		Cookies.set(getAuthenticationCookieName(key), String(value), authCookieOptions);
		return;
	}

	Cookies.set(key, String(value), options);
};

const removeCookie = (key: string, options: Cookies.CookieAttributes = {}) => {
	Cookies.remove(key, options);

	if (isAuthenticationCookie(key)) {
		const authCookieOptions = { ...options };

		if (config.authentication_cookies_domain) {
			authCookieOptions.domain = config.authentication_cookies_domain;
		}

		Cookies.remove(getAuthenticationCookieName(key), authCookieOptions);
	}
};

const requestAccessToken = async (): Promise<{
	access_token: string;
	expires_in?: number;
}> => {
	if (
		!config.api_url ||
		!config.oauth_client_id ||
		!config.oauth_client_secret ||
		!config.oauth_username ||
		!config.oauth_password
	) {
		throw new Error('OAuth config is incomplete');
	}

	const formData = new FormData();

	formData.append('client_id', config.oauth_client_id);
	formData.append('client_secret', config.oauth_client_secret);
	formData.append('grant_type', 'password');
	formData.append('username', config.oauth_username);
	formData.append('password', config.oauth_password);

	const response = await fetch(joinBaseUrl(config.api_url, ROUTES_API.OAUTH2_TOKEN), {
		method: 'POST',
		body: formData,
		credentials: 'include',
	});
	const responseText = await response.text();

	if (!response.ok) {
		throw new Error(responseText || `Login request failed with status ${response.status}`);
	}

	const parsedResponse = responseText
		? (JSON.parse(responseText) as {
				access_token?: string;
				expires_in?: number;
		  })
		: {};

	if (!parsedResponse.access_token) {
		throw new Error('No access token returned from auth server');
	}

	return {
		access_token: parsedResponse.access_token,
		expires_in: parsedResponse.expires_in,
	};
};

export const getAccessToken = (): string | undefined => {
	const token = getCookie(COOKIES.accessToken);
	const temporaryToken = getCookie(COOKIES.temporaryToken);

	return token || temporaryToken;
};

export const refreshAccessToken = async (): Promise<void> => {
	try {
		const { access_token, expires_in } = await requestAccessToken();

		setAccessToken(access_token, expires_in);
	} catch (error) {
		removeAccessToken();
		throw error;
	}
};

export const setAccessToken = (accessToken: string, expiresIn?: number): void => {
	setCookie(COOKIES.accessToken, accessToken, {
		expires: ACCESS_TOKEN_EXPIRATION_DAYS,
		secure: isSecure(),
	});

	if (expiresIn) {
		const expiresAt = Math.floor(Date.now() / 1000) + expiresIn;

		setCookie(COOKIES.tokenValidUntil, expiresAt, {
			expires: ACCESS_TOKEN_EXPIRATION_DAYS,
			secure: isSecure(),
		});
	}
};

export const setTemporaryAccessToken = (token: string): void => {
	setCookie(COOKIES.temporaryToken, token, {
		expires: ACCESS_TOKEN_EXPIRATION_DAYS,
		secure: isSecure(),
	});
};

export const removeAccessToken = (): void => {
	removeCookie(COOKIES.secureSession, { path: '/', secure: true });
	removeCookie(COOKIES.accessToken);
	removeCookie(COOKIES.tokenValidUntil);
};

export const removeTemporaryAccessToken = (): void => {
	removeCookie(COOKIES.temporaryToken);
};

export const isAuthenticated = (): boolean => {
	return !!getCookie(COOKIES.accessToken);
};

export const getTokenData = (): Record<string, unknown> | null => {
	try {
		const token = getCookie(COOKIES.accessToken);

		if (!token) {
			return null;
		}

		const tokenPayload = token.split('.')[1];

		if (!tokenPayload) {
			return null;
		}

		return JSON.parse(atob(tokenPayload));
	} catch (error) {
		if (error instanceof TypeError || error instanceof SyntaxError) {
			return null;
		}

		throw error;
	}
};

export const checkExpiredAccessToken = (): Promise<void> => {
	const timestamp = getCookie(COOKIES.tokenValidUntil);

	if (!timestamp) {
		return Promise.resolve();
	}

	const expiryDate = new Date(Number(timestamp) * 1000);

	if (new Date() > expiryDate) {
		return refreshAccessToken();
	}

	return Promise.resolve();
};
